
import { supabase } from './supabase';
import { Lead } from '../types/lead';

/**
 * Get all leads (for admins) or user's leads (for regular users)
 */
export const getLeads = async (userId?: string): Promise<Lead[]> => {
  try {
    console.log('🔍 Fetching leads for user:', userId || 'all users (admin)');

    let query = supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    // If userId is provided, filter by assigned user
    if (userId) {
      query = query.eq('assigned_user_id', userId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching leads:', error);
      throw error;
    }

    console.log(`✅ Loaded ${data?.length || 0} leads`);
    return (data as Lead[]) || [];
  } catch (error) {
    console.error('❌ Exception fetching leads:', error);
    return [];
  }
};

/**
 * Create a new lead
 */
export const createLead = async (leadData: Omit<Lead, 'id' | 'created_at'>): Promise<{ success: boolean; error?: string; leadId?: string }> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .insert({
        ...leadData,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    return {
      success: true,
      leadId: data.id
    };
  } catch (error: any) {
    console.error('Error creating lead:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Update a lead
 */
export const updateLead = async (leadId: string, updates: Partial<Lead>): Promise<boolean> => {
  try {
    console.log('📝 Updating lead:', leadId, 'with:', updates);

    // Only send updatable fields, exclude readonly fields like id, created_at, etc.
    const { id, created_at, facebook_lead_id, assigned_user_id, ...updateFields } = updates as any;

    const { data, error } = await supabase
      .from('leads')
      .update({
        ...updateFields,
        updated_at: new Date().toISOString(),
      })
      .eq('id', leadId)
      .select();

    if (error) {
      console.error('❌ Error updating lead:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return false;
    }

    console.log('✅ Lead updated successfully:', data);
    return true;
  } catch (error) {
    console.error('❌ Exception updating lead:', error);
    return false;
  }
};

/**
 * Delete a lead
 */
export const deleteLead = async (leadId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', leadId);

    if (error) {
      console.error('Error deleting lead:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting lead:', error);
    return false;
  }
};

/**
 * Get lead statistics for ALL users in one query, grouped by assigned_user_id
 */
export const getAllUserLeadStats = async (): Promise<Record<string, { total: number; neu: number; kontaktiert: number; gesprochen: number; termin_fixiert: number; abgeschlossen: number; kein_interesse: number }>> => {
  const { data, error } = await supabase
    .from('leads')
    .select('assigned_user_id, status');
  if (error) { console.error('Error fetching all user lead stats:', error); return {}; }

  const result: Record<string, any> = {};
  for (const row of (data || [])) {
    const uid = row.assigned_user_id;
    if (!uid) continue;
    if (!result[uid]) result[uid] = { total: 0, neu: 0, kontaktiert: 0, gesprochen: 0, termin_fixiert: 0, abgeschlossen: 0, kein_interesse: 0 };
    result[uid].total++;
    if (row.status in result[uid]) result[uid][row.status]++;
  }
  return result;
};

/**
 * Get lead statistics for a user (with all status categories)
 */
export const getLeadStats = async (userId?: string) => {
  try {
    let query = supabase.from('leads').select('status');

    if (userId) {
      query = query.eq('assigned_user_id', userId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching lead stats:', error);
      return {
        total: 0,
        neu: 0,
        kontaktiert: 0,
        gesprochen: 0,
        termin_fixiert: 0,
        abgeschlossen: 0,
        kein_interesse: 0
      };
    }

    const total = data?.length || 0;
    const neu = data?.filter(l => l.status === 'neu').length || 0;
    const kontaktiert = data?.filter(l => l.status === 'kontaktiert').length || 0;
    const gesprochen = data?.filter(l => l.status === 'gesprochen').length || 0;
    const termin_fixiert = data?.filter(l => l.status === 'termin_fixiert').length || 0;
    const abgeschlossen = data?.filter(l => l.status === 'abgeschlossen').length || 0;
    const kein_interesse = data?.filter(l => l.status === 'kein_interesse').length || 0;

    return {
      total,
      neu,
      kontaktiert,
      gesprochen,
      termin_fixiert,
      abgeschlossen,
      kein_interesse
    };
  } catch (error) {
    console.error('Error fetching lead stats:', error);
    return {
      total: 0,
      neu: 0,
      kontaktiert: 0,
      gesprochen: 0,
      termin_fixiert: 0,
      abgeschlossen: 0,
      kein_interesse: 0
    };
  }
};
